import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hostMetadataQueryKey, type TableExplorerMetadataEditorProviders } from './tableExplorerHostPorts';

export type RelationshipEditorTable = {
  schema: string;
  name: string;
  columns: { name: string; dataType: string }[];
};

type Props = {
  providers: TableExplorerMetadataEditorProviders;
  connectionId: string;
  database: string;
  children: (tables: RelationshipEditorTable[]) => ReactNode;
};

/**
 * The host's (Azure Table) table list for a loose-FK editor opened from an FK cell (S4 review F, F6). It is
 * rendered only while that editor is open, so an FK cell never carries an idle query observer for it, and SQL
 * cells never render it at all. The entry is cached under the host scope with the same key as the global
 * editor dialog's listing (review D, D10), so both reuse one read.
 */
export function HostRelationshipTables({ providers, connectionId, database, children }: Props) {
  const query = useQuery({
    queryKey: hostMetadataQueryKey(providers, connectionId, 'tables', database),
    queryFn: () => providers.listTables({ connectionId, database }),
    staleTime: 5 * 60 * 1000,
  });
  const tables = useMemo<RelationshipEditorTable[]>(
    () => (query.data ?? []).map((table) => ({ schema: table.schema, name: table.name, columns: [] })),
    [query.data],
  );
  return <>{children(tables)}</>;
}
